import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HeartHandshake, Send, Heart } from "lucide-react";
import { toast } from "sonner";

const CARD_TINTS = [
  "bg-[#6A0DAD]/6 border-[#6A0DAD]/30",
  "bg-[#D4AF37]/8 border-[#D4AF37]/30",
  "bg-[#6B8EAC]/8 border-[#6B8EAC]/30",
];

export default function SupportWall() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(
    () => api.get("/support-messages").then((r) => setItems(r.data)).catch(() => {}),
    []
  );
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!message.trim()) return toast.error("Please write a short note of support");
    setSending(true);
    try {
      await api.post("/support-messages", {
        name: name.trim() || (user ? user.name : ""),
        message: message.trim(),
      });
      toast.success("Thank you for showing you care 💜");
      setName("");
      setMessage("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <HeartHandshake className="text-[#6A0DAD]" size={30} />
        <h1 className="font-display text-4xl md:text-5xl text-[#1A202C]">Wall of Support</h1>
      </div>
      <p className="text-[#4A5568] mb-8 max-w-2xl leading-relaxed">
        You don't have to be a NICU family to care. Whether you've walked this road or simply want to
        send love and strength — leave a note. Every kind word matters. 💜
      </p>

      {/* Compose */}
      <div className="bg-white border border-[#6A0DAD] rounded-2xl p-6 shadow-sm mb-10">
        <p className="font-heading font-semibold text-[#1A202C] mb-3">Leave a note of encouragement</p>
        <input
          data-testid="support-name-input"
          placeholder={user ? `Posting as ${user.name} (edit if you like)` : "Your name (optional)"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#58098F] mb-3"
        />
        <textarea
          data-testid="support-message-input"
          placeholder="Share your words of hope, strength, or solidarity…"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#58098F]"
        />
        <div className="flex justify-end mt-3">
          <button
            data-testid="support-submit-btn"
            onClick={post}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors disabled:opacity-60"
          >
            <Send size={16} /> {sending ? "Sending…" : "Share support"}
          </button>
        </div>
        <p className="text-xs text-[#4A5568] mt-2">No account needed — everyone is welcome to leave love here.</p>
      </div>

      {/* Wall */}
      {items.length === 0 ? (
        <div className="bg-white border border-[#6A0DAD] rounded-2xl p-12 text-center text-[#4A5568]">
          Be the first to leave a note of support. 💜
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
          {items.map((m, i) => (
            <div
              key={m._id}
              data-testid={`support-card-${m._id}`}
              className={`break-inside-avoid rounded-2xl border p-5 ${CARD_TINTS[i % CARD_TINTS.length]}`}
            >
              <Heart size={16} className="text-[#6A0DAD] mb-2" fill="#6A0DAD" />
              <p className="text-[#1A202C] leading-relaxed">{m.message}</p>
              <p className="text-xs text-[#4A5568] font-heading mt-3">— {m.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
